import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { GraphQLTagValuesQuery } from '~src/catalog/fetchers/GraphQLTagValuesQuery';
import { FlowOutputStepRuns } from '~src/flow/resources/FlowOutputStepRuns';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import {
  GetFieldValuesForMixedContentRequest,
  GetFieldValuesRequest
} from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import {
  getSessionManagementServiceInstance,
  SessionManagementService
} from '~src/sessionManagement/SessionManagementService';
import { FilterPanel, FilterPanelProps } from '~src/spacesPlatform/components/filterPanel/FilterPanel';
import { getExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { FlowOutputStepRunContentType, MixedContentType } from '~src/spacesPlatform/models/ContentTypes';
import { FilterFieldNames, IFilterClause } from '~src/spacesPlatform/models/IFilterClause';
import { FilterPanelService, IFilterDefinition } from '~src/spacesPlatform/services/FilterPanelService';

import {
  CollectionFieldValuesQuery,
  CollectionItemsFieldValuesQuery
} from '../../../Collections/ResourceQuery/FieldValuesQuery';
import { removeFilterClauseFromFilters } from '../../actions/filters/FilterHelpers';
import { getFilterClausesForTypes } from '../../actions/filters/getFilterClausesForTypes';
import { FilterMap } from '../../state/ContentExplorerState';
import {
  isBackgroundJobsSpace,
  isCollectionsSpace,
  isFavoritesSpace,
  isFlowSpace,
  isMyCollectionsSubspace,
  isPersonalSpace,
  isProjectSpace,
  isRecentsSpace,
  isSingleCollectionSpace,
  isSiteAccelerationCollectionsSubspace,
  isSiteSpace,
  isSiteUserCollectionsSubspace,
  isSiteUserSpace,
  isSiteUsersSpace,
  isSomeKindaSearchSpace,
  isTableCatalogSpace,
  NullProjectIdForNonProjects,
  SpaceInfo
} from '../../state/SpaceInfo';

const DATABASE_STRING = 'database';
const TABLE_STRING = 'table';
export interface AdvancedFilterPanelProps extends Pick<FilterPanelProps, 'children'> {
  readonly mixedContentFilterMap: FilterMap;
  readonly spaceInfo: SpaceInfo;
  readonly shownContentTypes: ReadonlyArray<ExplorableItemType>;
  readonly transformFilterDefinitions?: (filterDefinitions: IFilterDefinition<any>[]) => void;
}

// This method can be moved potentially to spaces (or some level up) because
// all it's doing is calculating filter keys based on parameters
// and then the hack around transformFilterDefinitions will no longer be needed.
function getFilterDefinitions(
  isSessionServerAdmin: boolean,
  spaceInfo: SpaceInfo,
  shownContentTypes: ReadonlyArray<ExplorableItemType>,
  transformFilterDefinitions?: (filterDefinitions: IFilterDefinition<any>[]) => void
): IFilterDefinition<any>[] {
  let filterKeys: ContentFilterKeyName[];

  if (shownContentTypes.length === 1) {
    filterKeys = getExplorableItemDefinition(shownContentTypes[0]).contentFilterKeys;
  } else {
    // no types or multiple types - show mixed content filters
    filterKeys = MixedContentType.filterKeys;
    if (!isRecentsSpace(spaceInfo) && !isSingleCollectionSpace(spaceInfo)) {
      // recents page and collection supports the tags filter for mixed content
      filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.tag);
    }
  }

  if (isTableCatalogSpace(spaceInfo)){
    if (getSessionManagementServiceInstance().tablesInProjectsUIEnabled()){
      if(filterKeys.indexOf(ContentFiltersKeys.location)> -1){
        filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.location);
      }
      filterKeys.push(ContentFiltersKeys.location);
    }
  }

  if (isBackgroundJobsSpace(spaceInfo)) {
    // The showSystemJobs filter is only available for server admin users
    if (!isSessionServerAdmin) {
      filterKeys = _.without(filterKeys, 'showSystemJobs');
    }
  }

  // If not at top level (so project place), remove the top-level-only keys
  const FilterKeysForTopLevelOnly = [
    ContentFiltersKeys.project,
    ContentFiltersKeys.allProjects
  ];
  if (!isSiteSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, (key) => _.contains(FilterKeysForTopLevelOnly, key));
  }

  // remove favorites filter when on favorites space
  if (isFavoritesSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.favorite);
  }

  if (isPersonalSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.usersPersonalSpaceContent);
    filterKeys = _.reject(filterKeys, key => key === ContentFiltersKeys.owner);
  }

  // remove recents filter when on recents space
  if (isRecentsSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.recent);
  }

  if (isFlowSpace(spaceInfo)) {
    filterKeys = FlowOutputStepRunContentType.filterKeys;
  }

  if (isSiteUserSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, (key) => key === ContentFiltersKeys.owner);
  }

  if (isMyCollectionsSubspace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, key => key === ContentFiltersKeys.owner);
  }

  if (isProjectSpace(spaceInfo)){
    filterKeys = _.reject(filterKeys, key =>key === ContentFiltersKeys.location);
  }
  if (isSingleCollectionSpace(spaceInfo)) {
    filterKeys = _.reject(filterKeys, key =>
      // See Feature-level TFS 1172357 for all ga-silver related stories
      key === ContentFiltersKeys.hasAlert
      // Datasource specific filters (collections ga-silver)
      || key === ContentFiltersKeys.connectionType
      || key === ContentFiltersKeys.includeLiveToDataEngine
      || key === ContentFiltersKeys.extractStatus // Live or Extract
      || key === ContentFiltersKeys.showEmbedded  // Data source dropdown
      || key === ContentFiltersKeys.serverPort
      // Workbook specific filters
      || key === ContentFiltersKeys.workbookConnFilter
      || key === ContentFiltersKeys.recent
      // Location is just not supported in collection space
      || key === ContentFiltersKeys.location
    );
  }

  const filterDefinitions = FilterPanelService.getFilterDefinitions(filterKeys);

  if (transformFilterDefinitions) {
    transformFilterDefinitions(filterDefinitions);
  }

  return filterDefinitions;
}

export class AdvancedFilterPanel extends React.Component<AdvancedFilterPanelProps> {

  public static TestId = 'advanced-filter-panel';

  private sessionManagementService: SessionManagementService;

  private memoizedGetFilterDefinitions = defaultMemoize(getFilterDefinitions);

  public constructor(props: AdvancedFilterPanelProps) {
    super(props);
    this.sessionManagementService = getSessionManagementServiceInstance();
  }

  private isShowingOneContentType(): boolean {
    return this.props.shownContentTypes.length === 1;
  }

  private isShowingTableContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'table');
  }

  private isShowingDatabaseContent(): boolean {
    return this.props.shownContentTypes.every(t => t === 'database');
  }

  private getFieldValueQueryForShownContentTypes(filterField: string): any {
    if (this.isShowingDatabaseContent()) {
      if (filterField === FilterFieldNames.projectId){
        const fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
        return new FieldValuesQuery(fieldValuesFetcher, filterField, DATABASE_STRING);
      }
      return new GraphQLTagValuesQuery('database');
    } else if (this.isShowingTableContent()) {
      if (filterField === FilterFieldNames.projectId) {
        return new FieldValuesQuery(new WebClientListResourceFetcher(GetFieldValuesRequest,
                                    ServerApiMapper.fieldValues),
                                    filterField, TABLE_STRING);
      }
      return new GraphQLTagValuesQuery('table');
    }

    // Collections have a different API for field values -> directly to CS.
    if (isSingleCollectionSpace(this.props.spaceInfo)) {
      return new CollectionItemsFieldValuesQuery(this.props.spaceInfo.item.luid, filterField);
    }

    if (isCollectionsSpace(this.props.spaceInfo) || isSiteUserCollectionsSubspace(this.props.spaceInfo)) {
      return new CollectionFieldValuesQuery(filterField);
    }

    if (this.isShowingOneContentType()) {
      const fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
      const resourceType = getExplorableItemDefinition(this.props.shownContentTypes[0]).fieldValueResourceType;
      return new FieldValuesQuery(fieldValuesFetcher, filterField, resourceType);
    } else {
      // use mixed content api
      const fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesForMixedContentRequest, ServerApiMapper.fieldValues);
      return new FieldValuesQuery(fieldValuesFetcher, filterField);
    }
  }

  private getFieldValueQuery = (filterField: string): FieldValuesQuery => {
    if (this.props.spaceInfo.subspaceName === 'runHistory') {
      let filterClauses: IFilterClause[] = _.chain(this.props.mixedContentFilterMap)
                                            .pick(FlowOutputStepRunContentType.filterKeys)
                                            .values()
                                            .compact()
                                            .value();

      filterClauses = removeFilterClauseFromFilters(filterClauses, FilterFieldNames.flowOutputStepName);
      filterClauses.push(Filter.stringFieldEq(FilterFieldNames.flowId, this.props.spaceInfo.item.id));

      return FlowOutputStepRuns.valuesForField(FilterFieldNames.flowOutputStepName).withFilters(filterClauses);
    }

    let filtersForType: IFilterClause[] = getFilterClausesForTypes(
        this.props.mixedContentFilterMap,
        this.props.shownContentTypes,
        this.props.shownContentTypes,
        isSiteUserSpace(this.props.spaceInfo) ? NullProjectIdForNonProjects : this.props.spaceInfo.item.id,
        this.props.spaceInfo
    );

    // need to add the project filter clause so we don't get everything on the site/server for mixed content project space
    if (!this.isShowingOneContentType() && this.props.spaceInfo.item.contentType === 'project' && this.props.spaceInfo.item.id) {
      const projectIdFilter = ServerFilterClauses.serverFilterClauses.project(this.props.spaceInfo.item.id);
      filtersForType.push(projectIdFilter);
    }

    // add the favorites=true filter when in favorites space
    if (isFavoritesSpace(this.props.spaceInfo)) {
      const favoritesFilter = ServerFilterClauses.serverFilterClauses.favorite('true');
      filtersForType.push(favoritesFilter);

    } else if (isRecentsSpace(this.props.spaceInfo)) {
      const recentsFilter = ServerFilterClauses.serverFilterClauses.recent('true');
      filtersForType.push(recentsFilter);
    }

    // if we are searching for projects or in the site - projects space we want to get field values for all projects not just top level ones
    if (isSomeKindaSearchSpace(this.props.spaceInfo) && this.props.shownContentTypes.length === 1 && this.props.shownContentTypes[0] === 'project') {
      filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
    }

    if (isSiteSpace(this.props.spaceInfo) && (this.props.spaceInfo.subspaceName === 'workbook' || this.props.spaceInfo.subspaceName === 'view')) {
      filtersForType = removeFilterClauseFromFilters(filtersForType, 'containerType');
    }

    if (isSiteSpace(this.props.spaceInfo) && this.props.spaceInfo.subspaceName === 'project') {
      filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
    }

    if (isSiteUserSpace(this.props.spaceInfo)) {
      filtersForType = removeFilterClauseFromFilters(filtersForType, FilterFieldNames.topLevelProject);
      filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(this.props.spaceInfo.item.id));
    }

    const sessionManagementService = getSessionManagementServiceInstance();
    const userId = sessionManagementService.sessionUserId();

    if (isMyCollectionsSubspace(this.props.spaceInfo)) {
      filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(userId));
    }

    if (isPersonalSpace(this.props.spaceInfo)) {
      filtersForType.push(ServerFilterClauses.serverFilterClauses.containerType('PERSONAL_SPACE'));
      filtersForType.push(ServerFilterClauses.serverFilterClauses.owner(userId));
    }

    if(isSiteUsersSpace(this.props.spaceInfo)) {
      filtersForType.push(ServerFilterClauses.serverFilterClauses.isTabID('true'));
    }

    // We want to remove the field we are looking for so we can get all the possibilities back for it.
    // If the filter has no field, it obviously does not match the current field we want, so keep it.
    const filterClausesWithoutQueriedField = removeFilterClauseFromFilters(filtersForType, filterField);

    const fieldValuesQuery: FieldValuesQuery = this.getFieldValueQueryForShownContentTypes(filterField);
    return fieldValuesQuery.withFilters(filterClausesWithoutQueriedField).withMatchText('');
  };

  private shouldHideSearchForSpaceInfo(): boolean {
    return isSomeKindaSearchSpace(this.props.spaceInfo)
      || isBackgroundJobsSpace(this.props.spaceInfo)
      || (isSiteAccelerationCollectionsSubspace(this.props.spaceInfo))
      ;
  }

  public render(): JSX.Element {
    const filterDefinitions = this.memoizedGetFilterDefinitions(
      this.sessionManagementService.isSessionServerAdmin(),
      this.props.spaceInfo,
      this.props.shownContentTypes,
      this.props.transformFilterDefinitions
    );
    return <FilterPanel
      children={this.props.children}
      contentType={this.isShowingOneContentType() ? this.props.shownContentTypes[0] : MixedContentType.name}
      filterDefinitions={filterDefinitions}
      hideSearch={this.shouldHideSearchForSpaceInfo()}
      getFieldValueQuery={this.getFieldValueQuery}
    />;
  }
}
